// TMOS Action Detail Page
// Date: December 9, 2025
// Purpose: Detailed view of a single action with all related information
//
// Features:
// - Full action details
// - Related entities (Risks, Issues, Projects)
// - Comments section
// - History timeline
// - Assignment management

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { actionService, Action, ActionComment, ActionHistory } from '@/services/actionService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, XCircleIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';
import { toast } from 'react-hot-toast';

export const ActionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [action, setAction] = useState<Action | null>(null);
  const [comments, setComments] = useState<ActionComment[]>([]);
  const [history, setHistory] = useState<ActionHistory[]>([]);
  const [formattedHistory, setFormattedHistory] = useState<ActionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  
  // User assignment state
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<Array<{ id: string; name: string; email: string }>>([]);
  // Removed unused userSearchLoading state
  const [allUsers, setAllUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const userSearchRef = useRef<HTMLDivElement>(null);

  // Load action data and users on mount
  useEffect(() => {
    if (id) {
      loadActionData();
      // Load users for history UUID replacement
      if (allUsers.length === 0 && !usersLoading) {
        loadAllUsers();
      }
    }
  }, [id]);

  // Re-format history when allUsers is loaded
  useEffect(() => {
    if (history.length > 0 && allUsers.length > 0) {
      const formatted = history.map(entry => ({
        ...entry,
        old_value: formatHistoryValue(entry.old_value),
        new_value: formatHistoryValue(entry.new_value),
        comment: entry.comment ? formatHistoryValue(entry.comment) : entry.comment
      }));
      setFormattedHistory(formatted);
    } else {
      setFormattedHistory(history);
    }
  }, [history, allUsers]);

  /**
   * Load action, comments, and history
   */
  const loadActionData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const userName = currentUser?.firstName && currentUser?.lastName 
        ? `${currentUser.firstName} ${currentUser.lastName}`.trim()
        : currentUser?.email || 'Unknown';
      const userEmail = currentUser?.email || 'N/A';
      console.log(`[ActionDetailPage] Loading action data: actionId=${id}, userName=${userName}, userEmail=${userEmail}`);

      // Load action
      const actionData = await actionService.getActionById(id);
      setAction(actionData);
      console.log(`[ActionDetailPage] Action loaded: actionId=${id}, userName=${userName}, userEmail=${userEmail}, title=${actionData.title}, status=${actionData.status}`);

      // Load comments
      const commentsData = await actionService.getActionComments(id);
      setComments(commentsData);
      console.log(`[ActionDetailPage] Comments loaded: actionId=${id}, userName=${userName}, userEmail=${userEmail}, count=${commentsData.length}`);

      // Load history (if user is creator)
      try {
        const historyData = await actionService.getActionHistory(id);
        setHistory(historyData);
        console.log(`[ActionDetailPage] History loaded: actionId=${id}, userName=${userName}, userEmail=${userEmail}, count=${historyData.length}`);
      } catch (err) {
        // User might not be creator, history is optional
        console.log(`[ActionDetailPage] Could not load history for actionId=${id}, userName=${userName}, userEmail=${userEmail}:`, err);
      }
    } catch (err: any) {
      console.error(`[ActionDetailPage] Error loading action data: actionId=${id}, error=`, err);
      setError(err.message || 'Failed to load action');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle adding comment
   */
  const handleAddComment = async () => {
    if (!id || !newComment.trim()) return;

    try {
      const comment = await actionService.addComment(id, newComment);
      setComments(prev => [...prev, comment]);
      setNewComment('');
    } catch (err: any) {
      console.error('Error adding comment:', err);
      setError(err.message || 'Failed to add comment');
    }
  };

  /**
   * Load all users for assignment
   */
  const loadAllUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await api.get('/users/admin/users', {
        params: { limit: 1000 } // Get all users in tenant
      });
      
      if (response.data.success) {
        const users = response.data.data?.users || response.data.data || [];
        // Filter to current tenant only (unless platform admin)
        const filteredUsers = users
          .filter((u: any) => !currentUser?.tenantId || u.tenant_id === currentUser.tenantId)
          .map((u: any) => ({
            id: u.id,
            name: `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim() || u.email || 'Unknown User',
            email: u.email || ''
          }));
        
        // Ensure current user is always in the list
        if (currentUser?.id) {
          const currentUserInList = filteredUsers.find((u: any) => u.id === currentUser.id);
          if (!currentUserInList) {
            filteredUsers.unshift({
              id: currentUser.id,
              name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || 'You',
              email: currentUser.email || ''
            });
          }
        }
        
        setAllUsers(filteredUsers);
        // Exclude already assigned users from search results
        const assignedUserIds = action?.assigned_users?.map(a => a.user_id) || [];
        setUserSearchResults(filteredUsers.filter((u: any) => !assignedUserIds.includes(u.id)));
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
      // Even if API fails, ensure current user is available
      if (currentUser?.id) {
        const currentUserObj = {
          id: currentUser.id,
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || 'You',
          email: currentUser.email || ''
        };
        setAllUsers([currentUserObj]);
        const assignedUserIds = action?.assigned_users?.map(a => a.user_id) || [];
        if (!assignedUserIds.includes(currentUser.id)) {
          setUserSearchResults([currentUserObj]);
        }
      }
    } finally {
      setUsersLoading(false);
    }
  };

  /**
   * Search users for assignment (filter from already loaded users)
   */
  // Removed unused searchUsers function - using inline search logic instead

  /**
   * Load users when assign button is clicked
   */
  useEffect(() => {
    if (showAssignUser && allUsers.length === 0 && !usersLoading) {
      loadAllUsers();
    }
  }, [showAssignUser]);

  /**
   * Handle user search input change - filter loaded users
   */
  useEffect(() => {
    if (showAssignUser && allUsers.length > 0) {
      const timeoutId = setTimeout(() => {
        if (!userSearchTerm || userSearchTerm.length < 1) {
          // If no query, show all unassigned users
          const assignedUserIds = action?.assigned_users?.map(a => a.user_id) || [];
          setUserSearchResults(allUsers.filter((u: any) => !assignedUserIds.includes(u.id)));
        } else {
          // Filter loaded users by search query
          const queryLower = userSearchTerm.toLowerCase();
          const assignedUserIds = action?.assigned_users?.map(a => a.user_id) || [];
          const filtered = allUsers.filter((u: any) => 
            !assignedUserIds.includes(u.id) &&
            (u.name.toLowerCase().includes(queryLower) || u.email.toLowerCase().includes(queryLower))
          );
          setUserSearchResults(filtered);
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [userSearchTerm, showAssignUser, allUsers, action]);

  /**
   * Handle assigning user to action
   */
  const handleAssignUser = async (userId: string) => {
    if (!id) return;

    try {
      await actionService.assignAction(id, [userId]);
      await loadActionData(); // Reload to get updated assignments
      setShowAssignUser(false);
      setUserSearchTerm('');
      setUserSearchResults([]);
      toast.success(t('actions.userAssigned') || 'User assigned successfully');
    } catch (err: any) {
      console.error('Error assigning user:', err);
      toast.error(err.message || t('actions.failedToAssignUser') || 'Failed to assign user');
    }
  };

  /**
   * Handle unassigning user from action
   */
  const handleUnassignUser = async (userId: string) => {
    if (!id) return;

    try {
      await actionService.unassignAction(id, userId);
      await loadActionData(); // Reload to get updated assignments
      toast.success(t('actions.userUnassigned') || 'User unassigned successfully');
    } catch (err: any) {
      console.error('Error unassigning user:', err);
      toast.error(err.message || t('actions.failedToUnassignUser') || 'Failed to unassign user');
    }
  };

  /**
   * Close user search dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userSearchRef.current && !userSearchRef.current.contains(event.target as Node)) {
        setShowAssignUser(false);
        setUserSearchTerm('');
        setUserSearchResults([]);
      }
    };

    if (showAssignUser) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAssignUser]);

  /**
   * Replace UUIDs in history values with user names
   */
  const formatHistoryValue = (value: string | null | undefined): string => {
    if (!value) return '';
    
    // UUID pattern: 8-4-4-4-12 hexadecimal characters
    const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
    
    return value.replace(uuidPattern, (uuid) => {
      // Try to find user in allUsers (loaded for assignment dropdown)
      const user = allUsers.find(u => u.id === uuid);
      if (user) {
        return user.name || user.email || uuid;
      }
      // If not found, return UUID (shouldn't happen in practice)
      return uuid;
    });
  };

  /**
   * Get status icon
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-5 h-5 text-[var(--color-success)]" />;
      case 'IN_PROGRESS':
        return <ClockIcon className="w-5 h-5 text-[var(--color-info)]" />;
      case 'CANCELLED':
        return <XCircleIcon className="w-5 h-5 text-[var(--color-secondary-text)]" />;
      default:
        return <ClockIcon className="w-5 h-5 text-[var(--color-secondary-text)] opacity-75" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-[var(--color-secondary-text)]">{t('actions.loading') || 'Loading...'}</div>
      </div>
    );
  }

  if (error || !action) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="bg-[var(--color-surface-bg)] border-0 shadow-none p-4">
            <div className="text-[var(--color-error)]">{error || t('actions.notFound') || 'Action not found'}</div>
            <button
              onClick={() => navigate('/actions')}
              className="mt-4 px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] rounded hover:opacity-90"
            >
              {t('actions.back') || 'Back to Actions'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-[var(--color-surface-bg)] border-0 shadow-none p-4">
          {/* Header */}
          <div className="mb-4">
            <button
              onClick={() => navigate('/actions')}
              className="flex items-center gap-2 text-[var(--color-secondary-text)] hover:text-[var(--color-primary-bg)] mb-2"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              {t('actions.back') || 'Back'}
            </button>
            <div className="flex items-center gap-2 mb-1">
              {getStatusIcon(action.status)}
              <h1 className="text-xl font-bold text-[var(--color-surface-text)]">{action.title}</h1>
            </div>
            <p className="text-xs text-[var(--color-secondary-text)] ml-7">
              {t('actions.detailSubtitle') || 'Action details and related information'}
            </p>
          </div>

          {/* Action Details */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Status and Priority */}
              <div>
                <label className="text-xs font-semibold text-[var(--color-secondary-text)]">
                  {t('actions.status.label') || 'Status'}
                </label>
                <div className="mt-1">{action.status}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-[var(--color-secondary-text)]">
                  {t('actions.priority.label') || 'Priority'}
                </label>
                <div className="mt-1">{action.priority}</div>
              </div>

              {/* Due Date */}
              {action.due_date && (
                <div>
                  <label className="text-xs font-semibold text-[var(--color-secondary-text)]">
                    {t('actions.dueDate') || 'Due Date'}
                  </label>
                  <div className="mt-1">{new Date(action.due_date).toLocaleDateString()}</div>
                </div>
              )}

              {/* Created By */}
              <div>
                <label className="text-xs font-semibold text-[var(--color-secondary-text)]">
                  {t('actions.createdBy') || 'Created By'}
                </label>
                <div className="mt-1">{action.creator_name || 'Unknown User'}</div>
              </div>
            </div>

            {/* Description */}
            {action.description && (
              <div className="mt-4">
                <label className="text-xs font-semibold text-[var(--color-secondary-text)]">
                  {t('actions.description') || 'Description'}
                </label>
                <div className="mt-1 text-sm text-[var(--color-surface-text)] whitespace-pre-wrap">
                  {action.description}
                </div>
              </div>
            )}

            {/* Assigned Users */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-[var(--color-secondary-text)]">
                  {t('actions.assignedTo') || 'Assigned To'}
                </label>
                <button
                  onClick={() => setShowAssignUser(!showAssignUser)}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-[var(--color-primary-bg)] text-[var(--color-secondary-font)] rounded hover:opacity-90"
                >
                  <PlusIcon className="w-3 h-3" />
                  {t('actions.assignUser') || 'Assign User'}
                </button>
              </div>
              
              {/* Current Assignments */}
              {action.assigned_users && action.assigned_users.length > 0 ? (
                <div className="mt-1 space-y-1">
                  {action.assigned_users.map((assignment) => (
                    <div key={assignment.id} className="flex items-center justify-between p-2 bg-[var(--color-secondary-bg)] rounded text-sm">
                      <span>{assignment.user_name?.trim() || 'Unknown User'}</span>
                      <button
                        onClick={() => handleUnassignUser(assignment.user_id)}
                        className="text-[var(--color-error)] hover:text-[var(--color-error)]/90"
                        title={t('actions.unassign') || 'Unassign'}
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-1 text-sm text-[var(--color-secondary-text)] italic">
                  {t('actions.noAssignments') || 'No users assigned'}
                </div>
              )}

              {/* User Search Dropdown */}
              {showAssignUser && (
                <div ref={userSearchRef} className="mt-2 relative">
                  <input
                    type="text"
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    placeholder={t('actions.searchUsers') || 'Search users...'}
                    className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                    autoFocus
                  />
                  {usersLoading && (
                    <div className="absolute right-3 top-2.5 text-[var(--color-secondary-text)] text-xs">
                      {t('actions.loading') || 'Loading...'}
                    </div>
                  )}
                  {!usersLoading && userSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {userSearchResults.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleAssignUser(user.id)}
                          className="w-full text-left px-3 py-2 hover:bg-[var(--color-secondary-bg)] text-sm text-[var(--color-surface-text)]"
                        >
                          <div className="font-medium">{user.name}</div>
                          {user.email && (
                            <div className="text-xs text-[var(--color-secondary-text)]">{user.email}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {!usersLoading && userSearchTerm && userSearchResults.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md shadow-lg p-3 text-sm text-[var(--color-secondary-text)]">
                      {t('actions.noUsersFound') || 'No users found'}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Related Contexts */}
            {action.contexts && action.contexts.length > 0 && (
              <div className="mt-4">
                <label className="text-xs font-semibold text-[var(--color-secondary-text)]">
                  {t('actions.relatedContexts') || 'Related Contexts'}
                </label>
                <div className="mt-1">
                  {action.contexts.map((context) => (
                    <div key={context.id} className="text-sm">
                      {context.context_type}: {context.context_id}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="mb-6 border-t pt-4">
            <h2 className="text-lg font-semibold mb-4">{t('actions.comments') || 'Comments'}</h2>

            {/* Add Comment */}
            <div className="mb-4">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t('actions.addCommentPlaceholder') || 'Add a comment...'}
                className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md text-sm"
                rows={3}
              />
              <button
                onClick={handleAddComment}
                className="mt-2 px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-secondary-font)] rounded-md hover:opacity-90 text-sm"
              >
                {t('actions.addComment') || 'Add Comment'}
              </button>
            </div>

            {/* Comments List */}
            <div className="space-y-3">
              {comments.map((comment) => (
                <div key={comment.id} className="p-3 bg-[var(--color-secondary-bg)] rounded-md">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-[var(--color-surface-text)]">{comment.user_name || 'Unknown User'}</span>
                    <span className="text-xs text-[var(--color-secondary-text)]">
                      {new Date(comment.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--color-surface-text)]">{comment.comment_text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* History Section (if available) */}
          {(formattedHistory.length > 0 || history.length > 0) && (
            <div className="border-t pt-4">
              <h2 className="text-lg font-semibold mb-4">{t('actions.history') || 'History'}</h2>
              <div className="space-y-2">
                {(formattedHistory.length > 0 ? formattedHistory : history).map((entry) => (
                  <div key={entry.id} className="p-3 bg-[var(--color-secondary-bg)] rounded-md text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-[var(--color-surface-text)]">{(entry.user_name?.trim() || entry.user_email)}</span>
                      <span className="text-xs text-[var(--color-secondary-text)]">
                        {new Date(entry.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-[var(--color-secondary-text)]">
                      {entry.change_type}: {entry.old_value || ''} → {entry.new_value || ''}
                    </div>
                    {entry.comment && (
                      <div className="mt-1 text-[var(--color-surface-text)]">{entry.comment}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

